import React, { useState } from "react";
import { toast } from "sonner";
import { Copy, Check, Radio } from "lucide-react";

const BASE = `${window.location.origin}/api`;

const VARS = [
  ["CLAUDE_CODE_ENABLE_TELEMETRY", "1"],
  ["OTEL_METRICS_EXPORTER", "otlp"],
  ["OTEL_LOGS_EXPORTER", "otlp"],
  ["OTEL_EXPORTER_OTLP_PROTOCOL", "http/json"],
  ["OTEL_EXPORTER_OTLP_ENDPOINT", BASE],
  ["OTEL_EXPORTER_OTLP_HEADERS", "Authorization=Bearer <YOUR_INGEST_TOKEN>"],
];

const MODES = [
  { key: "ps-session", label: "PowerShell (this session)" },
  { key: "ps-permanent", label: "PowerShell (permanent)" },
  { key: "cmd-permanent", label: "CMD (permanent)" },
];

function buildSnippet(mode, token) {
  const vars = VARS.map(([k, v]) => [k, k === "OTEL_EXPORTER_OTLP_HEADERS" ? `Authorization=Bearer ${token}` : v]);
  if (mode === "ps-session") {
    return vars.map(([k, v]) => (k === "CLAUDE_CODE_ENABLE_TELEMETRY" ? `$env:${k}=${v}` : `$env:${k}="${v}"`)).join("\n");
  }
  if (mode === "ps-permanent") {
    return vars.map(([k, v]) => `[Environment]::SetEnvironmentVariable("${k}","${v}","User")`).join("\n")
      + `\n\n# Restart PowerShell (or sign out/in) so new sessions pick these up.`;
  }
  // cmd-permanent
  return vars.map(([k, v]) => `setx ${k} "${v}"`).join("\n")
    + `\n\nREM setx writes permanent user vars. Open a NEW window afterwards; they don't affect the current one.`;
}

export default function SetupPanel() {
  const [mode, setMode] = useState("ps-session");
  const [copied, setCopied] = useState(false);
  const snippet = buildSnippet(mode, "<YOUR_INGEST_TOKEN>");

  const copy = () => {
    navigator.clipboard.writeText(snippet).then(
      () => { setCopied(true); toast.success("Config copied to clipboard"); setTimeout(() => setCopied(false), 1500); },
      () => toast.error("Copy failed — select the text manually")
    );
  };

  return (
    <div className="bg-card border border-border p-5" data-testid="setup-panel">
      <div className="flex items-center gap-2 mb-3">
        <Radio className="w-4 h-4 text-green-500" />
        <p className="overline">connect claude code · windows</p>
      </div>
      <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
        Choose how to set the exporter variables, then replace{" "}
        <span className="font-mono text-white">&lt;YOUR_INGEST_TOKEN&gt;</span> with your bearer token (or use a per-developer token's "copy setup" above, which fills the token in automatically).
      </p>

      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="setup-mode-toggle">
        {MODES.map((m) => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            data-testid={`setup-mode-${m.key}`}
            className={`px-3 py-1.5 font-mono text-[11px] border transition-colors ${
              mode === m.key
                ? "border-white bg-white text-black"
                : "border-border bg-black/50 text-muted-foreground hover:text-white hover:border-white/40"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div className="relative">
        <button
          onClick={copy}
          data-testid="copy-config-button"
          className="absolute top-2 right-2 flex items-center gap-1.5 px-2 py-1 border border-border bg-black hover:bg-secondary font-mono text-[11px] text-muted-foreground hover:text-white transition-colors"
        >
          {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
          {copied ? "copied" : "copy"}
        </button>
        <pre data-testid="setup-snippet" className="text-[11px] text-zinc-300 bg-black border border-border p-4 overflow-x-auto whitespace-pre">{snippet}</pre>
      </div>
    </div>
  );
}
